import { Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Copy,
  ExternalLink,
  Info,
  Mail,
  MailX,
  Phone,
  PhoneOff,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react'
import { VerificationOfEmployment } from 'types/graphql'

import DnTable from 'src/components/Core/DnTable'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { toast } from 'src/components/Overlays/Toast/Toast'
import ContactCountBadge, {
  ContactBadgeInput,
} from 'src/components/VoE/ContactCountBadge/ContactCountBadge'
import NextActionTag from 'src/components/VoE/NextActionTag/NextActionTag'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import type { VoEStatus } from 'src/graphql/types/verificationOfEmployments'
import IconEmptyInbox from 'src/icons/IconEmptyInbox'
import { getEmployerClassificationLabel } from 'src/lib/employerClassification.utils'
import { formatSnakeValue } from 'src/lib/formatters'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'
import { buildFmcsaCarrierUrl } from 'src/lib/url.utils'
import { knownPlatformLabel } from 'src/lib/voeProvider.utils'
import {
  DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS,
  isExpiredEmployment,
} from 'src/lib/voeVerificationPeriod'

dayjs.extend(utc)

import VoEActionsMenu from '../VoEActionsMenu/VoEActionsMenu'
import VoEStatusMenu, { colorByStatus } from '../VoEStatusMenu/VoEStatusMenu'
import VoEVoiceAgentButton, {
  companyCanUseVoEVoiceAgent,
} from '../VoEVoiceAgentButton/VoEVoiceAgentButton'

/**
 * The VoE row as the shared contact badge reads it: the same rule and words
 * as the employment table, mapped from the VoE's own field names.
 */
export const voeContactBadgeInput = (
  voe: VerificationOfEmployment
): ContactBadgeInput => ({
  dotNumber: voe.dotNumber,
  carrierContactCount: voe.carrierContactCount,
  contactName: voe.contactName,
  email: voe.email,
  phone: voe.phoneNumber,
  suppressedContactTypes: [
    voe.contactSuppression?.email ? 'EMAIL' : null,
    voe.contactSuppression?.phoneNumber ? 'PHONE' : null,
    voe.contactSuppression?.faxNumber ? 'FAX' : null,
  ].filter((type): type is string => Boolean(type)),
})

export interface VoEApplicationTableProps {
  voeList?: VerificationOfEmployment[]
  loading?: boolean
  onContact: (voe) => void
  onCall: (voe) => void
  onEmail: (voe) => void
  onViewDetail: (voe) => void
  onDelete: (voeId) => void
  onRowClick: (voe: VerificationOfEmployment) => void
  onStatusChange?: (voe: VerificationOfEmployment, status: VoEStatus) => void
  statusUpdating?: boolean
  guardContact?: (voe: VerificationOfEmployment, action: () => void) => void
  verificationPeriodMonths?: number
}

const VoEApplicationTable = ({
  loading,
  voeList = [],
  onContact,
  onCall,
  onEmail,
  onViewDetail,
  onDelete,
  onRowClick,
  onStatusChange,
  statusUpdating = false,
  guardContact,
  verificationPeriodMonths = DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS,
}: VoEApplicationTableProps) => {
  const currentCompany = useCurrentCompanyContext()
  const voiceAgentAvailable = companyCanUseVoEVoiceAgent(currentCompany)

  const columns = [
    {
      key: 'voeId',
      label: '',
      width: 3,
      render: (voe: VerificationOfEmployment) => (
        <Tooltip label="Copy VoE ID">
          <Copy
            size={12}
            strokeWidth={1}
            className="cursor-pointer text-doubleNickel-gray-400 hover:text-doubleNickel-gray-600"
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(voe.voeId)
              toast('Copied VoE ID to clipboard', 'success')
            }}
          />
        </Tooltip>
      ),
    },
    {
      key: 'companyName',
      label: 'Company Name',
      width: 19,
      render: (voe: VerificationOfEmployment) => {
        const classificationReason = getEmployerClassificationLabel(
          voe.employerClassification
        )
        const tooltipText = voe.dotNumber
          ? `${voe.companyName} · DOT: ${voe.dotNumber}`
          : voe.companyName
        const platformLabel = knownPlatformLabel(
          voe.carrierRouting?.preferredPlatform
        )
        return (
          <div className="flex w-full items-center gap-1.5">
            <Tooltip label={tooltipText} position="top" openDelay={300}>
              <span className="min-w-0 flex-1 truncate">{voe.companyName}</span>
            </Tooltip>
            {classificationReason && (
              <span className="flex-shrink-0">
                <StyledBadge
                  color="gray"
                  compact="xs"
                  tooltipText={`Not routed for outreach — ${classificationReason}`}
                >
                  Skipped Company
                </StyledBadge>
              </span>
            )}
            {/* Same badge as the outbound table: how this employer answers
                verifications, shown with the employer rather than the contact. */}
            {platformLabel && (
              <span className="ml-auto flex-shrink-0">
                <StyledBadge
                  color="blue"
                  compact="xs"
                  tooltipText={`This employer answers verifications through ${platformLabel}`}
                >
                  {platformLabel}
                </StyledBadge>
              </span>
            )}
            {voe.dotNumber && (
              <Tooltip
                label={`FMCSA lookup · DOT ${voe.dotNumber}`}
                position="top"
              >
                <a
                  href={buildFmcsaCarrierUrl(voe.dotNumber)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`FMCSA lookup for DOT ${voe.dotNumber}`}
                  onClick={(e) => e.stopPropagation()}
                  className={`flex-shrink-0 ${platformLabel ? '' : 'ml-auto'}`}
                >
                  <ExternalLink className="h-3 w-3 text-doubleNickel-gray-400 hover:text-doubleNickel-brand-500" />
                </a>
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: 12,
      render: (voe: VerificationOfEmployment) =>
        onStatusChange ? (
          <VoEStatusMenu
            status={voe.status}
            disabled={statusUpdating}
            onSelect={(status) => onStatusChange(voe, status)}
          />
        ) : (
          <StyledBadge
            textTransform="capitalize"
            color={colorByStatus[voe.status]}
          >
            {formatSnakeValue(voe.status)}
          </StyledBadge>
        ),
    },
    {
      key: 'nextAction',
      label: 'Next action',
      width: 15,
      render: (voe: VerificationOfEmployment) => <NextActionTag voe={voe} />,
    },
    {
      key: 'empPeriod',
      label: 'Employment',
      width: 12,
      render: (voe: VerificationOfEmployment) => {
        const startDate = voe.requestContent?.startDate
        const endDate = voe.requestContent?.endDate

        if (!startDate) {
          return <span className="text-xs text-doubleNickel-gray-400">—</span>
        }

        const startFormatted = dayjs(startDate).utc().format('MM/YYYY')
        const endFormatted = endDate
          ? dayjs(endDate).utc().format('MM/YYYY')
          : 'Present'
        const expired = isExpiredEmployment(endDate, verificationPeriodMonths)

        return (
          <Tooltip
            label={`${dayjs(startDate).utc().format('MMM YYYY')} – ${
              endDate ? dayjs(endDate).utc().format('MMM YYYY') : 'Present'
            }`}
            position="top"
          >
            <span
              className={`whitespace-nowrap text-xs ${
                expired
                  ? 'text-doubleNickel-gray-400 line-through'
                  : 'text-doubleNickel-gray-900'
              }`}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {startFormatted} – {endFormatted}
            </span>
          </Tooltip>
        )
      },
    },
    {
      key: 'shouldContactEmployer',
      // Wide enough for the consent answer plus a "3 contacts" badge
      width: 8,
      label: (
        <div className="flex items-center gap-1">
          <Tooltip
            label="Whether the applicant consented to contact this employer"
            position="top"
          >
            <span>Consent</span>
          </Tooltip>
        </div>
      ),
      // The consent answer, then the directory badge for a VoE with no
      // contact yet (renders nothing once the VoE has one). Only an explicit
      // yes counts as consent — a blank answer is "No" too.
      render: (voe: VerificationOfEmployment) => {
        const hasConsent = voe.shouldContactEmployer === true
        return (
          <div className="flex items-center gap-1.5">
            {hasConsent ? (
              <Tooltip
                label="Applicant consented to contact this employer"
                position="top"
              >
                <span className="flex items-center gap-1.5 text-xs text-doubleNickel-success-600">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Yes
                </span>
              </Tooltip>
            ) : (
              <Tooltip
                label="No consent to contact this employer"
                position="top"
              >
                <span className="flex items-center gap-1.5 text-xs text-doubleNickel-warning-500">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  No
                </span>
              </Tooltip>
            )}
            <ContactCountBadge
              item={voeContactBadgeInput(voe)}
              subject="verification"
            />
          </div>
        )
      },
    },
    {
      key: 'auditTrailLogs',
      width: 6,
      label: (
        <div className="flex items-center gap-1">
          <span>#</span>
          <Tooltip label="Contact Attempts" position="top">
            <Info className="h-3 w-3 text-doubleNickel-gray-400" />
          </Tooltip>
        </div>
      ),
      render: (voe: VerificationOfEmployment) =>
        (voe.auditTrailLogs || []).filter(
          (item) =>
            item.type === 'CALL' ||
            item.type === 'EMAIL' ||
            item.type === 'FAX' ||
            item.type === 'ATS'
        ).length,
    },
    {
      key: 'lastContactDate',
      label: 'Last Contact',
      width: 10,
      render: (voe: VerificationOfEmployment) =>
        voe.lastContactDate
          ? dayjs(voe.lastContactDate).format('MM/DD/YYYY')
          : '–',
    },
    {
      key: 'phone',
      label: '',
      width: 3,
      render: (voe: VerificationOfEmployment) => {
        const hasPhone = Boolean(voe.phoneNumber)
        const phoneSuppressed = Boolean(voe.contactSuppression?.phoneNumber)
        if (hasPhone && phoneSuppressed) {
          return (
            <Tooltip
              label={`${formatInternationalPhoneNumber(
                voe.phoneNumber
              )} — this contact asked not to be called`}
              position="top"
            >
              <PhoneOff
                aria-label="Phone suppressed"
                className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-warning-500"
              />
            </Tooltip>
          )
        }
        return hasPhone ? (
          <Tooltip
            label={formatInternationalPhoneNumber(voe.phoneNumber)}
            position="top"
          >
            <button
              onClick={(e) => {
                e.stopPropagation()
                onCall(voe)
              }}
            >
              <Phone className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-gray-500 hover:text-doubleNickel-brand-500" />
            </button>
          </Tooltip>
        ) : (
          <Tooltip label="No phone number" position="top">
            <Phone className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-error-400" />
          </Tooltip>
        )
      },
    },
    {
      key: 'email',
      label: '',
      width: 3,
      render: (voe: VerificationOfEmployment) => {
        const hasEmail = Boolean(voe.email)
        const emailSuppressed = Boolean(voe.contactSuppression?.email)
        if (hasEmail && emailSuppressed) {
          return (
            <Tooltip
              label={`${voe.email} — this contact asked not to be emailed`}
              position="top"
            >
              <MailX
                aria-label="Email suppressed"
                className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-warning-500"
              />
            </Tooltip>
          )
        }
        return hasEmail ? (
          <Tooltip label={voe.email} position="top">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEmail(voe)
              }}
            >
              <Mail className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-gray-500 hover:text-doubleNickel-brand-500" />
            </button>
          </Tooltip>
        ) : (
          <Tooltip label="No email address — click to enter one" position="top">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEmail(voe)
              }}
            >
              <Mail className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-error-400 hover:text-doubleNickel-brand-500" />
            </button>
          </Tooltip>
        )
      },
    },
    // Only companies with the VoE voice agent get the column — the button
    // itself decides which of their VoEs it can actually call.
    ...(voiceAgentAvailable
      ? [
          {
            key: 'voiceAgent',
            label: '',
            width: 3,
            render: (voe: VerificationOfEmployment) => (
              <VoEVoiceAgentButton
                voeId={voe.voeId}
                phoneNumber={voe.phoneNumber}
                email={voe.email}
                canceledOutOfWindow={voe.canceledOutOfWindow}
                callingHours={voe.callingHours}
                guardContact={
                  guardContact && ((startCall) => guardContact(voe, startCall))
                }
              />
            ),
          },
        ]
      : []),
    {
      key: 'actions',
      label: '',
      width: 4,
      render: (voe: VerificationOfEmployment) => (
        <VoEActionsMenu
          voe={voe}
          iconClassName="h-6 w-6"
          onContact={onContact}
          onViewDetail={onViewDetail}
          onDelete={onDelete}
        />
      ),
    },
  ]

  return (
    <div className="flex-1">
      <DnTable
        columns={columns}
        data={voeList}
        loading={loading}
        onRowClick={(rowIndex, voe) => onRowClick(voe)}
        emptyStateMessage={
          <div className="flex flex-1 flex-col items-center justify-center">
            <div className="text-md font-medium text-doubleNickel-gray-800">
              No Records Found
            </div>
            <div className="text-sm text-doubleNickel-gray-600">
              Make sure the application is marked as completed, and the consents
              & employment history are up to date.
            </div>
          </div>
        }
        emptyStateIcon={<IconEmptyInbox className="h-40 fill-none" />}
        headerClassName="h-11 bg-doubleNickel-gray-50 text-xs font-medium text-doubleNickel-gray-600"
        rowClassName="hover:bg-doubleNickel-gray-50"
      />
    </div>
  )
}

export default VoEApplicationTable
